import { useState, type ReactNode } from 'react';
import { Command } from 'cmdk';
import { Dialog as RDialog } from 'radix-ui';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { CornerDownLeft, Search } from 'lucide-react';
import { cn } from '../lib/cn';
import { useHotkey } from '../lib/hooks';
import { durations, easings, springs } from '../lib/motion';
import { Kbd, Spinner } from './display';

export interface CommandPaletteItem {
  id: string;
  label: string;
  icon?: ReactNode;
  /** Texto auxiliar à direita (ex.: "Produtos"). */
  hint?: ReactNode;
  /** Teclas do atalho, ex.: ['Ctrl', 'N'] ou 'F2'. */
  shortcut?: string[] | string;
  /** Palavras extras para a busca (sinônimos). */
  keywords?: string[];
  disabled?: boolean;
  onSelect: () => void;
}

export interface CommandPaletteGroup {
  heading: string;
  items: CommandPaletteItem[];
}

export interface CommandPaletteProps {
  groups: CommandPaletteGroup[];
  /** Controlado (opcional). Sem `open`, o componente cuida do próprio estado. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  placeholder?: string;
  emptyText?: ReactNode;
  /** Atalho global Ctrl/Cmd + K (padrão: ligado). */
  hotkey?: boolean;
  /** Conteúdo extra no rodapé (à esquerda das dicas de teclado). */
  footer?: ReactNode;
  /** Avisa a cada letra digitada (busca ao vivo na API). O campo continua controlado pelo componente. */
  onSearchChange?: (search: string) => void;
  /** Mostra um indicador de carregamento no campo (resultados ao vivo chegando). */
  loading?: boolean;
  /**
   * Pontuação de cada item (0 = esconde), no formato do cmdk. `value` vem como "rótulo__id".
   * Sem ela, vale a busca aproximada padrão do cmdk.
   */
  filter?: (value: string, search: string, keywords?: string[]) => number;
}

/** Paleta de comandos (Ctrl/Cmd + K): busca e executa ações e atalhos de navegação. */
export function CommandPalette({
  groups,
  open: openProp,
  onOpenChange,
  placeholder = 'Buscar ação ou tela',
  emptyText = 'Nada encontrado.',
  hotkey = true,
  footer,
  onSearchChange,
  loading,
  filter,
}: CommandPaletteProps) {
  const [inner, setInner] = useState(false);
  const open = openProp ?? inner;
  const setOpen = (v: boolean) => {
    if (openProp === undefined) setInner(v);
    onOpenChange?.(v);
  };
  const reduce = useReducedMotion();

  useHotkey(
    'mod+k',
    (e) => {
      e.preventDefault();
      setOpen(!open);
    },
    { enabled: hotkey, allowInInputs: true },
  );

  return (
    <RDialog.Root open={open} onOpenChange={setOpen}>
      <AnimatePresence>
        {open ? (
          <RDialog.Portal forceMount>
            <RDialog.Overlay asChild forceMount>
              <motion.div
                className="fixed inset-0 z-50 bg-scrim backdrop-blur-[3px]"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: durations.fast, ease: easings.standard }}
              />
            </RDialog.Overlay>
            <RDialog.Content asChild forceMount aria-describedby={undefined}>
              <motion.div
                className="fixed left-1/2 top-[12vh] z-50 w-[calc(100vw-1.5rem)] max-w-xl -translate-x-1/2 overflow-hidden rounded-sheet bg-elevated shadow-pop outline-none"
                initial={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.97, y: -8 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.98, transition: { duration: durations.fast } }}
                transition={reduce ? { duration: durations.fast } : springs.gentle}
              >
                <RDialog.Title className="sr-only">Paleta de comandos</RDialog.Title>
                <Command label="Paleta de comandos" loop filter={filter} className="flex max-h-[min(70vh,520px)] flex-col">
                  <div className="flex items-center gap-3 border-b border-line px-4">
                    <Search className="size-[18px] shrink-0 text-ink-3" aria-hidden="true" />
                    <Command.Input
                      autoFocus
                      placeholder={placeholder}
                      onValueChange={onSearchChange}
                      className="h-14 w-full bg-transparent text-[16px] text-ink outline-none placeholder:text-ink-3"
                    />
                    {loading ? <Spinner className="size-4 shrink-0 text-ink-3" /> : null}
                    <Kbd className="hidden sm:inline-flex">Esc</Kbd>
                  </div>
                  <Command.List className="scrollbar-thin min-h-0 flex-1 overflow-y-auto overscroll-contain p-2">
                    <Command.Empty className="px-3 py-10 text-center text-sm text-ink-2">{emptyText}</Command.Empty>
                    {groups.map((g) => (
                      <Command.Group
                        key={g.heading}
                        heading={g.heading}
                        className="[&_[cmdk-group-heading]]:eyebrow [&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:pb-1.5 [&_[cmdk-group-heading]]:pt-3 [&_[cmdk-group-heading]]:text-ink-3"
                      >
                        {g.items.map((it) => {
                          const keys = typeof it.shortcut === 'string' ? [it.shortcut] : it.shortcut;
                          return (
                            <Command.Item
                              key={it.id}
                              value={`${it.label}__${it.id}`}
                              keywords={it.keywords}
                              disabled={it.disabled}
                              onSelect={() => {
                                setOpen(false);
                                it.onSelect();
                              }}
                              className={cn(
                                'group flex h-11 cursor-pointer select-none items-center gap-3 rounded-xl px-3 text-[15px] text-ink outline-none transition-colors duration-100',
                                'data-[selected=true]:bg-ink/[0.06] data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-40 [&_svg]:size-[18px]',
                              )}
                            >
                              {it.icon ? <span className="flex shrink-0 text-ink-3 group-data-[selected=true]:text-primary-strong">{it.icon}</span> : null}
                              <span className="min-w-0 flex-1 truncate">{it.label}</span>
                              {it.hint ? <span className="shrink-0 text-xs text-ink-3">{it.hint}</span> : null}
                              {keys?.length ? (
                                <span className="flex shrink-0 items-center gap-1">
                                  {keys.map((k) => (
                                    <Kbd key={k}>{k}</Kbd>
                                  ))}
                                </span>
                              ) : null}
                            </Command.Item>
                          );
                        })}
                      </Command.Group>
                    ))}
                  </Command.List>
                  <div className="flex items-center gap-3 border-t border-line bg-surface-2/60 px-4 py-2.5 text-xs text-ink-3">
                    <div className="min-w-0 flex-1 truncate">{footer}</div>
                    <span className="hidden items-center gap-1.5 sm:flex">
                      <Kbd>↑</Kbd>
                      <Kbd>↓</Kbd> navegar
                    </span>
                    <span className="hidden items-center gap-1.5 sm:flex">
                      <Kbd>
                        <CornerDownLeft className="size-3" aria-hidden="true" />
                      </Kbd>
                      abrir
                    </span>
                  </div>
                </Command>
              </motion.div>
            </RDialog.Content>
          </RDialog.Portal>
        ) : null}
      </AnimatePresence>
    </RDialog.Root>
  );
}
